import { Box, Stack, styled, Typography } from '@mui/material';
import { MuiColorInput, MuiColorInputFormat } from 'mui-color-input';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

const defaultColors = [getColor(EColors.pureBlack), '#AB37B8', '#EA4335', '#FBBC05', '#34A853', '#4285F4', '#DADADA'];

const StyledColorInput = styled(MuiColorInput, {
    shouldForwardProp: prop => prop !== 'error',
})<{ error: boolean }>(({ error }) => ({
    flex: 1,
    boxSizing: 'border-box',
    width: '120px',
    '& .MuiInputBase-root': {
        height: '32px',
        paddingLeft: '9px',
        background: getColor(EColors.v3Background),
        borderRadius: '8px',
    },
    '& .MuiOutlinedInput-input': {
        padding: 0,
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '12px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid',
        borderColor: error ? getColor(EColors.red) : getColor(EColors.inactiveAction),
        borderRadius: '8px',
    },
    '& .MuiInputAdornment-root > .MuiButton-root.MuiButton-text': {
        borderRadius: '50%',
        height: '20px',
        width: '20px',
    },
}));

const ColoredPill = styled(Box, {
    shouldForwardProp: prop => prop !== 'backgroundColor' && prop !== 'selected',
})<{ backgroundColor: string; selected: boolean }>(({ backgroundColor, selected }) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1px solid white',
    backgroundColor,
    outline: selected ? `1px solid ${backgroundColor}` : 'none',
    cursor: 'pointer',
}));

type ColorInputProps = {
    value: string;
    format?: MuiColorInputFormat;
    error?: FieldError;
    disabled?: boolean;
    isAlphaHidden?: boolean;
    onChange: (color: string) => void;
};

export const ColorInput = React.forwardRef<HTMLDivElement, ColorInputProps>(
    ({ error, format = 'hex', value, onChange, disabled }, ref) => {
        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" gap={0.5} alignItems="center">
                        {defaultColors.map(defaultColor => (
                            <ColoredPill
                                key={defaultColor}
                                backgroundColor={defaultColor}
                                selected={defaultColor === value}
                                onClick={() => onChange(defaultColor)}
                            />
                        ))}
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500} lineHeight="100%">
                            Custom
                        </Typography>

                        <StyledColorInput
                            format={format}
                            error={!!error}
                            onChange={onChange}
                            value={value}
                            disabled={disabled}
                            ref={ref}
                            isAlphaHidden
                        />
                    </Stack>
                </Stack>
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error.message}
                    </Typography>
                )}
            </Box>
        );
    }
);
